import { BucketType } from '@/types/disposition';

/**
 * Which of the lead screens a table instance is rendering.
 *
 * EnhancedLeadsTable backs five different screens and told them apart by
 * calling apiEndpoint.includes('...') in ten places — several of them
 * re-deriving the same flag two thousand lines apart, and the four
 * column-visibility blocks each restating a full column list. Adding a screen,
 * or letting one screen return more than one row per customer, meant finding
 * every one of those sites and keeping them consistent by hand.
 *
 * This is the one place that answers "which screen am I", so the table reads
 * flags instead of re-parsing a URL.
 */
export type LeadScreen =
    | 'abandoned_cart'
    | 'reorder'
    | 'rto'
    | 'edd'
    | 'failed_delivery'
    | 'cod_confirm';

export interface BucketMode {
    screen: LeadScreen;
    /** Disposition tree to load. EDD and Failed Delivery share the 'edd' tree. */
    bucketType: BucketType;
    isAbandonedCart: boolean;
    isReorder: boolean;
    isRto: boolean;
    isEdd: boolean;
    isFailedDelivery: boolean;
    /** Confirm Orders: COD orders called before the courier picks them up. */
    isCodConfirm: boolean;
    /** EDD and Failed Delivery are the two shipment screens — same filter options. */
    isShipmentScreen: boolean;
    /**
     * Row-identity fields, most specific first. Resolved against a row with the
     * first one present winning, falling through to the lead's own uuid.
     *
     * Reorder already renders one row per due line item, so a customer with two
     * products due appears twice; `order_uuid` is here for the same reason on
     * the shipment screens, and is ignored until the API sends it.
     */
    rowIdFields: string[];
    /** Columns pushed to the end of the table, in this order. */
    dateColumnsAtEnd: string[];
    /** Column names to show on top of the shared defaults. */
    addColumns: string[];
    /** Column names to hide from the shared defaults. */
    removeColumns: string[];
}

const SHIPMENT_COLUMNS = [
    'order_name', 'order_source', 'product_info', 'payment_gateway', 'order_date',
    'awb', 'courier_partner', 'pickup_hub', 'dispatched_date', 'dispatch_ageing',
    'expected_delivery_date', 'current_status', 'risk',
];

const SHIPMENT_REMOVALS = [
    'customer_type', 'created_date', 'updated_date',
    'last_call_status', 'last_call_duration', 'address', 'email',
];

const ROW_ID_FALLBACK = ['uuid'];

const MODES: Record<LeadScreen, Omit<BucketMode,
    'screen' | 'isAbandonedCart' | 'isReorder' | 'isRto' | 'isEdd' | 'isFailedDelivery' | 'isCodConfirm' | 'isShipmentScreen'>> = {
    abandoned_cart: {
        bucketType: 'abandoned_cart',
        rowIdFields: ROW_ID_FALLBACK,
        dateColumnsAtEnd: ['created_date', 'updated_date'],
        addColumns: [],
        removeColumns: [],
    },
    reorder: {
        bucketType: 'reorder',
        // One row per due line item — a two-product order is two separate calls
        // on two separate dates.
        rowIdFields: ['line_item_uuid', ...ROW_ID_FALLBACK],
        dateColumnsAtEnd: ['last_abc_date', 'reorder_due_date'],
        addColumns: ['reorder_due_date'],
        removeColumns: ['product_info', 'address', 'customer_type', 'created_date', 'updated_date'],
    },
    rto: {
        bucketType: 'rto',
        rowIdFields: ['order_uuid', ...ROW_ID_FALLBACK],
        dateColumnsAtEnd: [
            'payment_gateway', 'order_date', 'rto_date', 'rto_status',
            'risk', 'profile_captured', 'updated_date',
        ],
        addColumns: ['payment_gateway', 'order_date', 'rto_date', 'rto_status', 'risk', 'updated_date'],
        removeColumns: ['product_info', 'address', 'customer_type', 'created_date'],
    },
    edd: {
        bucketType: 'edd',
        rowIdFields: ['order_uuid', ...ROW_ID_FALLBACK],
        // Ordering is governed end-to-end by EDD_COLUMN_ORDER in the table, so
        // nothing is pushed to the end here — one source of truth.
        dateColumnsAtEnd: [],
        addColumns: [
            ...SHIPMENT_COLUMNS,
            // EDD ageing supersedes Days Late here: it counts down before the
            // expected date and up after it, so it covers not-yet-late rows too.
            'edd_ageing', 'edd_priority',
        ],
        removeColumns: [...SHIPMENT_REMOVALS, 'days_late'],
    },
    failed_delivery: {
        bucketType: 'edd',
        rowIdFields: ['order_uuid', ...ROW_ID_FALLBACK],
        // Same as EDD — NDR_COLUMN_ORDER in the table is the single source of truth.
        dateColumnsAtEnd: [],
        addColumns: [
            ...SHIPMENT_COLUMNS,
            'days_late',
            // NDR 1/2/3 replace the single 'Failure Reason' column — same data,
            // but per attempt instead of only the latest.
            'ndr_1', 'ndr_2', 'ndr_3',
        ],
        removeColumns: [...SHIPMENT_REMOVALS, 'ndr_reason'],
    },
    cod_confirm: {
        bucketType: 'cod_confirm',
        rowIdFields: ['order_uuid', ...ROW_ID_FALLBACK],
        // COD_COLUMN_ORDER in the table is the single source of truth for ordering.
        dateColumnsAtEnd: [],
        addColumns: [
            'order_name', 'ships_by', 'cod_tier', 'customer_history', 'total_price',
            'order_source', 'product_info', 'payment_gateway', 'order_date',
        ],
        removeColumns: [...SHIPMENT_REMOVALS, 'last_abc_date'],
    },
};

/** Longest first: 'failed-delivery-leads' must win before any shorter match. */
const ENDPOINT_TO_SCREEN: ReadonlyArray<[string, LeadScreen]> = [
    ['failed-delivery-leads', 'failed_delivery'],
    ['reorder-leads', 'reorder'],
    ['rto-leads', 'rto'],
    ['edd-leads', 'edd'],
    ['cod-confirm-leads', 'cod_confirm'],
];

export function getBucketMode(apiEndpoint: string): BucketMode {
    const screen: LeadScreen =
        ENDPOINT_TO_SCREEN.find(([fragment]) => apiEndpoint.includes(fragment))?.[1]
        ?? 'abandoned_cart';

    return {
        screen,
        ...MODES[screen],
        isAbandonedCart: screen === 'abandoned_cart',
        isReorder: screen === 'reorder',
        isRto: screen === 'rto',
        isEdd: screen === 'edd',
        isFailedDelivery: screen === 'failed_delivery',
        isCodConfirm: screen === 'cod_confirm',
        isShipmentScreen: screen === 'edd' || screen === 'failed_delivery',
    };
}

/**
 * Identity for one table row.
 *
 * A screen that returns more than one row per customer needs a per-row key, but
 * the field carrying it differs by screen and only appears once the API sends
 * it — so this walks the screen's fields in order and falls back to the lead's
 * own uuid, which is what every screen used before.
 */
export function getRowId(mode: BucketMode, row: Record<string, any>): string {
    for (const field of mode.rowIdFields) {
        const value = row?.[field];
        if (value) return String(value);
    }
    return String(row?.uuid ?? '');
}
